// websocketService.js
import config from './config';

class WebSocketService {
    constructor(resource, stockIDs, updateStockData, setLastUpdateTime) {
      this.resource = resource;
      this.stockIDs = stockIDs;
      this.updateStockData = updateStockData;
      this.setLastUpdateTime = setLastUpdateTime;
      this.socket = null;
      this.intervalId = null;
      this.jsLastUpdate = null;
      this.heartbeatIntervalId = null;
      this.reconnectTimeoutId = null;
      this.openTimeoutId = null;
      this.reconnectAttempts = 0;
      this.isManuallyDisconnected = false;
      this.isConnecting = false;
      //console.log("service called"+stockIDs[0]);
    }

    // Helper function to get formatted timestamp
    getTimestamp() {
      return new Date().toLocaleString('en-IN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit',
        hour12: false 
      });
    }

    setIDs(ids){
      this.stockIDs = ids;
    }

    mergerIDs(newIds){
      //this.stockIDs = this.stockIDs.concat(newIds);
      this.stockIDs = [...new Set([...this.stockIDs, ...newIds])];
    }
  
    connect() {
      if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
        return;
      }
      if (this.isConnecting) {
        return;
      }

      this.isManuallyDisconnected = false;
      this.isConnecting = true;

      const url =
        this.resource === "zerodha"
          ? config.WS_ZERODHA_URL
          : config.ALT_FEED_WS_URL;
  
      this.socket = new WebSocket(url);

      if (this.openTimeoutId) {
        clearTimeout(this.openTimeoutId);
      }
      this.openTimeoutId = setTimeout(() => {
        if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
          console.warn(`[${this.getTimestamp()}] WebSocket open timeout, closing and retrying...`);
          this.socket.close();
        }
      }, 12000);
  
      this.socket.onopen = () => {
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        if (this.openTimeoutId) {
          clearTimeout(this.openTimeoutId);
          this.openTimeoutId = null;
        }

        console.log(`[${this.getTimestamp()}] WebSocket connection opened`);
  
        if (this.resource === "zerodha") {
          this.stockIDs.forEach((item) => {
            //console.log(item);
            const instrumentIdentifier = item;//Object.keys(item)[0]; //"CRUDEOILM24JULFUT";
            this.socket.send(JSON.stringify({ subscribe: instrumentIdentifier }));
            // this.socket.send(
            //   `42["subscribe_symbol",{"room":"${instrumentIdentifier}"}]`
            // );
          });
  
          this.startHeartbeat();
        } else {
          this.socket.send(
            JSON.stringify({ method: "subscribe", params: this.stockIDs })
          );
          this.startHeartbeat();
        }

        // Start the interval for the refresh check
        this.startRefreshCheck();

      };
  
      this.socket.onclose = () => {
        this.isConnecting = false;
        if (this.openTimeoutId) {
          clearTimeout(this.openTimeoutId);
          this.openTimeoutId = null;
        }
        this.stopHeartbeat();
        this.stopRefreshCheck();
        console.log(`[${this.getTimestamp()}] WebSocket connection closed`);
        if (!this.isManuallyDisconnected) {
          this.scheduleReconnect("close");
        }
      };
  
      this.socket.onmessage = (event) => {
        //this.jsLastUpdate = new Date().getTime
        this.handleMessage(event);
        //alert(event);
      };
  
      this.socket.onerror = (event) => {
        console.error(`[${this.getTimestamp()}] WebSocket error:`, event);
      };
    }
  
    handleMessage(event) {
      const response = event.data;
  
      let jsonData;
      try {
        if (response.includes("ping")) {
          this.socket.send(JSON.stringify("pong"));
        } else if (response.includes(42)) {
          const trimmedResponse = response.slice(2);
          jsonData = JSON.parse(trimmedResponse);
        } else {
          jsonData = JSON.parse(response);
        }
      } catch (error) {
        console.error("Error parsing JSON:", error);
      }
  
      if (jsonData) {
        if (this.resource === "zerodha" && jsonData[0] === "scrip_data") {
          const data = jsonData[1].data;
          this.updateStockData(data);
        } else {
          this.updateStockData(jsonData);
        }
      }
  
      const currentTime = new Date();
      this.setLastUpdateTime(currentTime);
      this.jsLastUpdate = currentTime; // Update the last update time
    }
  
    disconnect() {
      this.isManuallyDisconnected = true;
      this.isConnecting = false;

      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }
      if (this.openTimeoutId) {
        clearTimeout(this.openTimeoutId);
        this.openTimeoutId = null;
      }

      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
      // Stop the interval for the refresh check
      this.stopHeartbeat();
      this.stopRefreshCheck();
    }

    startHeartbeat() {
      this.stopHeartbeat();
      this.heartbeatIntervalId = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send("2");
        }
      }, 28000);
    }

    stopHeartbeat() {
      if (this.heartbeatIntervalId) {
        clearInterval(this.heartbeatIntervalId);
        this.heartbeatIntervalId = null;
      }
    }

    startRefreshCheck() {
      this.stopRefreshCheck();
      this.intervalId = setInterval(this.refreshCheck.bind(this), 5000);
    }
  
    stopRefreshCheck() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  
    refreshCheck() {
      if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
        this.scheduleReconnect("socket_closed");
        return;
      }

      if (this.jsLastUpdate != null) {
        const nowTime = new Date();
        const timeDiff = Math.abs(nowTime.getTime() - this.jsLastUpdate.getTime());
        const timeDiffInSecond = Math.ceil(timeDiff / 1000);
  
        if (timeDiffInSecond > 10) {
          console.log(`[${this.getTimestamp()}] WebSocket reconnecting due to timeout (${timeDiffInSecond}s since last update)`);
          this.socket.close();
        }
      }
    }

    scheduleReconnect(reason = "unknown") {
      if (this.isManuallyDisconnected || this.reconnectTimeoutId) {
        return;
      }

      const delayMs = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      this.reconnectAttempts += 1;
      console.log(
        `[${this.getTimestamp()}] Scheduling WebSocket reconnect in ${delayMs}ms (reason: ${reason}, attempt: ${this.reconnectAttempts})`
      );

      this.reconnectTimeoutId = setTimeout(() => {
        this.reconnectTimeoutId = null;
        if (!this.isManuallyDisconnected) {
          this.connect();
        }
      }, delayMs);
    }
  }
  
  export default WebSocketService;
  